import type { TransportType } from '@shared/transportTypeDefs.js';
import type { Feature, LineString, Position } from 'geojson';
import { hash } from 'ohash';
import z from 'zod';
import {
  GeoJSONFeatureGenericSchema,
  GeoJSONLineStringSchema,
  GeoJSONPositionSchema,
  GeoJSONPropertiesSchema,
} from 'zod-geojson';
import {
  type Alternative,
  AlternativeSchema,
  type RoutePoint,
  type RoutingMode,
  type Step,
  type StepCoordinate,
  WaypointSchema,
} from './actions.js';
import type { RoutePlannerState } from './reducer.js';
import { flattenWithStructures } from './structureElevation.js';

const LineFeatureSchema = GeoJSONFeatureGenericSchema(
  GeoJSONPositionSchema,
  GeoJSONPropertiesSchema.nullable(),
  GeoJSONLineStringSchema,
);

/**
 * The computed route a saved map carries, so opening the map draws what was
 * planned instead of asking the router again — which is what makes a map
 * usable offline, and what stops a saved route from silently changing as the
 * routing graph does.
 */
export const SavedRouteSchema = z.object({
  /** {@link routeKey} of the waypoints, mode and transport this belongs to. */
  key: z.string(),
  /** When it was computed; also discriminates the drawn layers per result. */
  timestamp: z.number(),
  /** The active alternative only — the others are a transient offer. */
  alternative: AlternativeSchema,
  waypoints: z.array(WaypointSchema),
  /**
   * The DEM-sampled, densified elevation line, so the profile, ascent/descent
   * and elevation colorize work offline. Levelling and smoothing are applied at
   * render, so those preferences stay live. Absent where the alternative's own
   * coordinates already carry the same elevation.
   */
  geometry: LineFeatureSchema.optional(),
});

export type SavedRoute = z.infer<typeof SavedRouteSchema>;

/**
 * Identifies the route a result belongs to. Shared with the URL's
 * `route-params-hash` and with the multimodal check in the find-route handler,
 * so a stored result is matched against exactly what the router was asked.
 *
 * The points are reduced to their values first. The digest counts a key holding
 * `undefined` — which the reducer readily produces (`routePlannerSetStart`) and
 * which `JSON.stringify` then drops — so hashing the objects themselves makes a
 * route read as a different one purely for having been through storage or the
 * URL. An absent transport and an empty one likewise have to be one thing, since
 * that is how the URL spells it.
 */
export function routeKey(route: {
  points: RoutePoint[];
  mode: RoutingMode;
  transportType: TransportType;
}): string {
  return hash([
    route.points.map(({ lat, lon, transport }) => [
      lat,
      lon,
      transport || null,
    ]),
    route.mode,
    route.transportType,
  ]);
}

/**
 * Coordinate precision a saved route is written at: ~0.1 m horizontally and
 * 0.1 m of elevation. A multi-day route is the target use case, and past this
 * the extra digits are only bytes.
 */
const coordDecimals = 6;

const eleDecimals = 1;

function round(value: number, decimals: number): number {
  const scale = 10 ** decimals;

  return Math.round(value * scale) / scale;
}

function roundCoordinate([lon, lat, ele]: StepCoordinate): StepCoordinate {
  return ele === undefined
    ? [round(lon, coordDecimals), round(lat, coordDecimals)]
    : [
        round(lon, coordDecimals),
        round(lat, coordDecimals),
        round(ele, eleDecimals),
      ];
}

function roundPosition(position: Position): Position {
  return roundCoordinate(position as StepCoordinate);
}

function roundAlternative(alternative: Alternative): Alternative {
  return {
    ...alternative,
    legs: alternative.legs.map((leg) => ({
      ...leg,
      steps: leg.steps.map(
        (step): Step => ({
          ...step,
          geometry: {
            coordinates: step.geometry.coordinates.map(roundCoordinate),
          },
        }),
      ),
    })),
  };
}

function roundLine(line: Feature<LineString>): Feature<LineString> {
  return {
    ...line,
    geometry: {
      ...line.geometry,
      coordinates: line.geometry.coordinates.map(roundPosition),
    },
  };
}

/**
 * Whether the sampled line says anything the alternative's own coordinates
 * don't. On the free tier a GraphHopper route is neither densified nor
 * re-sampled, so storing the line would only duplicate the route.
 */
function sampledLineIsWorthStoring(
  line: Feature<LineString>,
  alternative: Alternative,
): boolean {
  const { coordinates } = flattenWithStructures(alternative);

  return (
    coordinates.length !== line.geometry.coordinates.length ||
    line.geometry.coordinates.some(
      (coord, i) => coord[2] !== coordinates[i]![2],
    )
  );
}

/**
 * The route to store in a map document, or nothing when there is none to store.
 * Isochrones are areas rather than a route and are recomputed on open.
 */
export function savedRouteFromState(
  state: RoutePlannerState,
): SavedRoute | undefined {
  const alternative = state.alternatives[state.activeAlternativeIndex];

  if (!alternative || state.mode === 'isochrone' || state.timestamp === null) {
    return undefined;
  }

  const sampled = state.sampledGeojson?.line;

  return {
    key: routeKey(state),
    timestamp: state.timestamp,
    alternative: roundAlternative(alternative),
    waypoints: state.waypoints,
    ...(sampled && sampledLineIsWorthStoring(sampled, alternative)
      ? { geometry: roundLine(sampled) }
      : {}),
  };
}
